import type { OrgMemberRow } from 'pure-js/types/OrganizationMembership'
import { isParticipantOrgRole } from 'pure-js/libs/organizationMembershipHelpers'
import type { ReflectionSprintScopeData, ReflectionScopeTeam } from 'pure-js/types/Reflection'

// Pure helpers of the modal where the people a leader leads are chosen.

/** A whole team or a whole sprint, offered as a quick way to tick its people. */
export type LeaderScopeGroup = { id: string; label: string; userIds: string[] }

export const getOrgMemberLabel = (member: OrgMemberRow | null): string =>
  member?.displayName || member?.email || member?.userId || ''

/** The people who may be put under one leader: participants of this organization, minus the leader, by name. */
export function selectEligibleMembers(members: OrgMemberRow[], leaderUserId?: string): OrgMemberRow[] {
  return members
    .filter((member) => isParticipantOrgRole(member.role) && member.userId !== leaderUserId)
    .sort((first, second) => getOrgMemberLabel(first).localeCompare(getOrgMemberLabel(second)))
}

const getEligibleTeamUserIds = (team: ReflectionScopeTeam, eligibleUserIds: Set<string>): string[] =>
  team.participants.map((participant) => participant.userId).filter((userId) => eligibleUserIds.has(userId))

/** One group per team that has someone to add; a team that plays in several sprints is listed once. */
export function buildTeamGroups(
  sprints: ReflectionSprintScopeData[],
  eligibleUserIds: Set<string>
): LeaderScopeGroup[] {
  const groupsById = new Map<string, LeaderScopeGroup>()

  sprints
    .flatMap((sprint) => sprint.teams)
    .forEach((team) => {
      const userIds = getEligibleTeamUserIds(team, eligibleUserIds)
      if (userIds.length > 0) groupsById.set(team.id, { id: team.id, label: team.name, userIds })
    })

  return [...groupsById.values()]
}

/** One group per sprint that has someone to add, each person once however many teams they play in. */
export function buildSprintGroups(
  sprints: ReflectionSprintScopeData[],
  eligibleUserIds: Set<string>
): LeaderScopeGroup[] {
  return sprints
    .map((sprint) => ({
      id: sprint.id,
      label: sprint.title,
      userIds: [...new Set(sprint.teams.flatMap((team) => getEligibleTeamUserIds(team, eligibleUserIds)))]
    }))
    .filter((group) => group.userIds.length > 0)
}
